import { lazy, Suspense, useEffect, useState } from "react";
import { MonitorPlay } from "lucide-react";
import { api } from "../../lib/api";

// The player is loaded only when a page actually has this block.
const ScreenLoopPlayer = lazy(() => import("../smartScreens/ScreenLoopPlayer"));

/**
 * v149.88 — "My Screen Playback": the host's Smart Screen, played on their
 * Web Link. `config.screen_id` is one of the owner's screens (chosen in the
 * editor); the server only answers when that screen belongs to the page's
 * owner (`?owner=<slug>`). Each ad links to the advertiser's own Web Link for
 * its first seconds; plays and clicks are counted for the advertiser.
 */
export default function ScreenPlaybackBlock({ block, hubContext, editable = false }) {
  const cfg = block?.config || {};
  const slug = hubContext?.creator?.slug || hubContext?.slug || null;
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!cfg.screen_id || !slug) return;
    setError(false);
    api.get(`/public/smart-screens/${cfg.screen_id}/playback`, { params: { owner: slug } })
      .then(({ data: d }) => setData(d))
      .catch(() => setError(true));
  }, [cfg.screen_id, slug]);

  if (!cfg.screen_id || (!slug && editable)) {
    return editable ? (
      <div className="ah-block p-4 text-sm ah-muted flex items-center gap-2" data-testid={`block-screen-playback-empty-${block?.block_id}`}>
        <MonitorPlay className="w-4 h-4" /> {cfg.screen_id ? "Publish your page to preview your screen here." : "Pick which of your screens to show."}
      </div>
    ) : null;
  }
  if (error) return editable ? <p className="text-xs ah-muted">This screen can&apos;t be shown.</p> : null;
  // Visitors never see an empty black box: nothing published today → no block.
  if (data && !data.has_schedule && !editable) return null;
  return (
    <div className="ah-block p-3 space-y-2 min-w-0" data-testid={`block-screen-playback-${block?.block_id}`}>
      {cfg.heading ? <p className="font-bold" style={{ fontFamily: "var(--ah-title-family)" }}>{cfg.heading}</p> : null}
      {data ? (
        <Suspense fallback={<div className="w-full rounded-xl bg-black" style={{ aspectRatio: "16 / 9" }} />}>
          <ScreenLoopPlayer data={data} screenId={cfg.screen_id} track={!editable} page={slug} testid={`screen-playback-${block?.block_id}`} />
        </Suspense>
      ) : (
        <div className="w-full rounded-xl bg-black/80 animate-pulse" style={{ aspectRatio: "16 / 9" }} />
      )}
      {cfg.caption ? <p className="text-xs ah-muted">{cfg.caption}</p> : null}
    </div>
  );
}
